import { LoanInfosDto } from 'api/borrower';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { LightAction } from 'components/ui/LightAction';
import { createIcon } from 'components/icons/IconFactory';
import { AddIcon } from 'components/icons/Icons';
import { Sizes } from 'enums/sizes';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { NsKey } from 'enums/NsKey';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import {
  useBorrowerRiskData,
  useSubscriptionProcessState
} from 'features/subscription-process/contexts/selectors/selectors';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getBorrowerProjectDocuments } from 'services/borrowerProjectDocumentsAPI'
import { borrowersAPI } from 'services/swagger-api';
import { BorrowerLoanFormPanel } from '../../forms/borrower-loan';
import { BorrowerLoanCard } from './BorrowerLoanCard';

export const BorrowerProfilingLoanSection = () => {
  const { openPanel } = usePanel()
  const { t } = useTranslation(NsKey.Borrower)
  // Source de vérité du projet courant = l'URL. On NE se fie PAS à un id global
  // (riskId Redux) qui peut être périmé après changement de simulation, sinon le prêt
  // serait rattaché au mauvais projet.
  const { borrowerProjectId } = useParams<{ borrowerProjectId: string }>()

  const { riskId } = useSubscriptionProcessState()
  const riskData = useBorrowerRiskData()
  const loans = riskData?.loans
  const project = riskData?.project
  const insuranceProjectReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.InsuranceProject))

  const currentLoans = useMemo(() => {
    return loans
  }, [loans])

  const isCurrentLoansEmpty = useMemo(() => {
    if (currentLoans) {
      return currentLoans.length === 0
    }
    return true
  }, [currentLoans])

  const { refreshSubscriptionProcess } = useSubscriptionProcessActions()

  const isRenego = useMemo(() => {
    const ref = insuranceProjectReferential?.find(
      (ref) => ref.id === project?.insuranceProjectId
    )
    return ref?.code === 'RENEG_BANQUE' || ref?.code ==="RENEG_COURTIER"
  }, [insuranceProjectReferential, project?.insuranceProjectId])

  // Project documents are read before opening the panel: with at least one file, the panel is widened to show them beside the form.
  const loadProjectDocuments = useCallback(async () => {
    if (!borrowerProjectId) {
      return []
    }
    try {
      return (await getBorrowerProjectDocuments(borrowerProjectId)).documents ?? []
    } catch (error) {
      console.error('Error fetching project documents:', error)
      return []
    }
  }, [borrowerProjectId])

  const onCreateInsuredEntity = useCallback(async () => {
    const documents = await loadProjectDocuments()
    openPanel({
      title: `Prêt 1`,
      size: documents.some(d => d.storedFileId) ? Sizes['7xl'] : Sizes['2xl'],
    },
      <BorrowerLoanFormPanel onSuccess={refreshSubscriptionProcess} isRenego={isRenego} borrowerProjectId={borrowerProjectId} projectDocuments={documents} />
    )
  }, [openPanel, refreshSubscriptionProcess, isRenego, borrowerProjectId, loadProjectDocuments])

  const onUpdateInsuredEntity = useCallback(async (loan: LoanInfosDto, loanIndex: number) => {
    const documents = await loadProjectDocuments()
    openPanel({
      title: `Prêt ${loanIndex + 1}`,
      size: documents.some(d => d.storedFileId) ? Sizes['7xl'] : Sizes['2xl'],
    },
      <BorrowerLoanFormPanel onSuccess={refreshSubscriptionProcess} currentLoan={loan} isRenego={isRenego} borrowerProjectId={borrowerProjectId} projectDocuments={documents} />
    )
  }, [openPanel, refreshSubscriptionProcess, isRenego, borrowerProjectId, loadProjectDocuments])

  const onDeleteLoan = useCallback(async (loanId: string) => {
    try {
      await borrowersAPI.deleteLoan({ loanId });
      refreshSubscriptionProcess();
    } catch (error: any) {
      console.error('Error deleting loan:', error);
      toast.error(
        error?.response?.error?.message || 'Erreur lors de la suppression du prêt'
      );
    }
  }, [refreshSubscriptionProcess]);

  const PrimaryResidence = createIcon('commercial');

  return <CardsSection
    icon={<PrimaryResidence className="h-8 w-8 text-primary" />}
    title={t("BORROWER.LABELS.PROJECT_LOANS_DESCRIPTION")}
    titleAside={!isCurrentLoansEmpty && <LightAction icon={AddIcon} label={t("BORROWER.LABELS.ADD_LOAN")} onClick={() => onCreateInsuredEntity()} />}
  >
    <div className="flex items-stretch gap-4">
      {
        isCurrentLoansEmpty &&
        <div className="flex items-center justify-center bg-white w-full rounded-xl border p-8">
          <LightAction icon={AddIcon} label={t("BORROWER.LABELS.ADD_LOAN")} onClick={() => onCreateInsuredEntity()} />
        </div>
      }
      {
        !isCurrentLoansEmpty &&
        <div className="grid grid-cols-2 gap-4 w-full">
          {
            currentLoans?.map((entity: LoanInfosDto, index: number) => (
              <BorrowerLoanCard
                key={index}
                loan={entity}
                onUpdate={(loan) => onUpdateInsuredEntity(loan, index)}
                onDelete={onDeleteLoan}
              />
            ))
          }
        </div>
      }
    </div>
  </CardsSection>
}
